import React from 'react';
import { ShieldCheckIcon, FileCheckIcon, GavelIcon, GlobeIcon } from 'lucide-react';
import { Seo } from '../components/ui/Seo';
import { PageHero } from '../components/ui/PageHero';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Reveal } from '../components/ui/Reveal';
import { CtaBanner } from '../components/ui/CtaBanner';
import { VALUES } from '../data/content';
import { IMAGES } from '../data/images';
import { useLanguage } from '../i18n/LanguageContext';
const STANDARD_ICONS = [FileCheckIcon, ShieldCheckIcon, GavelIcon];
export function About() {
  const {
    lang,
    p
  } = useLanguage();
  const a = p.about;
  return <>
      <Seo title={a.seo.title} description={a.seo.description} data-id="element-330" data-mp="2sc7ljnh6uyvq" />
      <PageHero eyebrow={a.hero.eyebrow} title={a.hero.title} description={a.hero.description} breadcrumb={a.hero.breadcrumb} image={IMAGES.boardroom} data-id="element-331" data-mp="2x7wom0sf2xws" />

      <section className="w-full bg-white py-24 lg:py-32" data-id="element-332" data-mp="1luu9zb9voif9">
        <div className="mx-auto grid max-w-container gap-16 px-6 lg:grid-cols-12 lg:gap-20" data-id="element-333" data-mp="1uhibv6smo61y">
          <div className="lg:col-span-7" data-id="element-334" data-mp="1pip1vocjux6j">
            <SectionHeading eyebrow={a.storyEyebrow} title={a.storyTitle} data-id="element-335" data-mp="1pk0d298br77o" />
            <div className="mt-6 space-y-5 text-base leading-relaxed text-charcoal-light" data-id="element-336" data-mp="23iqbbs6vrcxm">
              {a.story.map(paragraph => <p key={paragraph.slice(0, 32)} data-id="element-337" data-mp="1yhtzmsgaemhn">{paragraph}</p>)}
            </div>
          </div>

          <div className="lg:col-span-5" data-id="element-338" data-mp="2dejl53t7ssbp">
            <Reveal data-id="element-339" data-mp="28i2xsbtuahzo">
              <div className="rounded-3xl border border-navy-100 bg-mist p-8 shadow-soft" data-id="element-340" data-mp="28ghzcb4emewj">
                <h2 className="eyebrow text-gold-dark" data-id="element-341" data-mp="2m7wkoolsas6r">{a.missionTitle}</h2>
                <p className="mt-4 font-display text-2xl leading-snug text-navy" data-id="element-342" data-mp="2i7md57icftps">{a.mission}</p>
              </div>
            </Reveal>
            <Reveal delay={0.1} data-id="element-343" data-mp="2w7xamte5s6jx">
              <div className="mt-6 rounded-3xl bg-navy p-8 shadow-soft" data-id="element-344" data-mp="2w4swp4aw5wg2">
                <h2 className="eyebrow text-gold" data-id="element-345" data-mp="2r9m0sinv5w1p">{a.visionTitle}</h2>
                <p className="mt-4 font-display text-2xl leading-snug text-white" data-id="element-346" data-mp="1ovfrsiehrnxx">{a.vision}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="w-full bg-mist py-24 lg:py-32" data-id="element-347" data-mp="1xvkwulw7qa8x">
        <div className="mx-auto max-w-container px-6" data-id="element-348" data-mp="1xu03gzlmwxcn">
          <SectionHeading eyebrow={a.valuesEyebrow} title={a.valuesTitle} align="center" data-id="element-349" data-mp="22mb1ty0q8b33" />
          <ul className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-350" data-mp="27lnpum3c3o79">
            {VALUES.map((value, i) => <Reveal as="li" key={value.copy.en.title} delay={i * 0.05} data-id="element-351" data-mp="2cj5zyft389gu" data-mpi={i}>
                <div className="h-full rounded-2xl border border-navy-100 bg-white p-7 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-lift" data-id="element-352" data-mp="2gm2dvt7onheg">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy-50 text-navy" data-id="element-353" data-mp="m81dh3whimc9">
                    <value.Icon className="h-5 w-5" aria-hidden="true" data-id="element-354" data-mp="r6covj2fh4wl" />
                  </span>
                  <h3 className="mt-6 font-display text-xl font-semibold text-navy" data-id="element-355" data-mp="jnvb30nir3xt">
                    {value.copy[lang].title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal-light" data-id="element-356" data-mp="evbxw4squatj">
                    {value.copy[lang].description}
                  </p>
                </div>
              </Reveal>)}
          </ul>
        </div>
      </section>

      <section className="relative w-full overflow-hidden bg-navy-950 py-24 lg:py-32" data-id="element-357" data-mp="12mhbsef4it17">
        <img src={IMAGES.network} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-35" data-id="element-358" data-mp="xmmm2e7349hq" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950 via-navy-950/85 to-navy-950" aria-hidden="true" data-id="element-359" data-mp="omgbp16onpms" />
        <div className="relative mx-auto grid max-w-container gap-16 px-6 lg:grid-cols-2" data-id="element-360" data-mp="3df21ciqoq38v">
          <div data-id="element-361" data-mp="39bm4pmem82w6">
            <SectionHeading eyebrow={a.reachEyebrow} title={a.reachTitle} description={a.reachDescription} tone="light" data-id="element-362" data-mp="39b41u6xg6qfa" />
            <Reveal delay={0.15} data-id="element-363" data-mp="15vhaa17t292l">
              <div className="mt-10 inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-5 py-3 text-sm text-navy-100/70" data-id="element-364" data-mp="15ws8zmme8qmu">
                <GlobeIcon className="h-4 w-4 text-gold" aria-hidden="true" data-id="element-365" data-mp="w5xq8s8cw965" />
                {a.reachBadge}
              </div>
            </Reveal>
          </div>
          <ul className="grid gap-4 sm:grid-cols-2" data-id="element-366" data-mp="1otjpjoajwrn7">
            {a.regions.map((r, i) => <Reveal as="li" key={r.region} delay={i * 0.05} data-id="element-367" data-mp="1atsjqau6uhka" data-mpi={i}>
                <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-colors duration-300 hover:border-gold/40" data-id="element-368" data-mp="1at8xwj1f390z">
                  <h3 className="font-display text-lg font-semibold text-white" data-id="element-369" data-mp="3vk9fqtnqryjq">{r.region}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-navy-100/60" data-id="element-370" data-mp="3fywijj7vnv5">{r.detail}</p>
                </div>
              </Reveal>)}
          </ul>
        </div>
      </section>

      <section className="w-full bg-white py-24 lg:py-32" data-id="element-371" data-mp="3n30s2xiuda2p">
        <div className="mx-auto max-w-container px-6" data-id="element-372" data-mp="3n2hchngnyxru">
          <SectionHeading eyebrow={a.standardsEyebrow} title={a.standardsTitle} align="center" data-id="element-373" data-mp="5vf3gemyl1k8" />
          <ul className="mt-16 grid gap-10 lg:grid-cols-3" data-id="element-374" data-mp="v9a8u11j701">
            {a.standards.map((s, i) => {
            const Icon = STANDARD_ICONS[i];
            return <Reveal as="li" key={s.title} delay={i * 0.06} data-id="element-375" data-mp="3999khuoo809m" data-mpi={i}>
                  <div className="border-t border-navy-100 pt-7" data-id="element-376" data-mp="34e2l2z2fphqi">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/50 text-gold-dark" data-id="element-377" data-mp="3j1nkoj03oyto">
                      <Icon className="h-5 w-5" aria-hidden="true" data-id="element-378" data-mp="3e94alw0mgltm" />
                    </span>
                    <h3 className="mt-5 font-display text-xl font-semibold text-navy" data-id="element-379" data-mp="2qjjnd0xfr2ej">{s.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-charcoal-light" data-id="element-380" data-mp="2lli5gjbqx94n">
                      {s.description}
                    </p>
                  </div>
                </Reveal>;
          })}
          </ul>
        </div>
      </section>

      <CtaBanner title={a.ctaTitle} description={a.ctaDescription} data-id="element-381" data-mp="3k1z1oaz74yl7" />
    </>;
}